<template>
  <div class="flex flex-col h-screen w-full pt-16 overflow-y-auto">
    <div class="flex w-full h-full">
      <div class="flex flex-col w-7/12 pt-4 items-center px-4">
        <h1 class="font-black mb-2 text-xl text-teal-900">Sentence</h1>
        <!-- SENTENCE DISPLAY PART -->
          <div
            class="h-full border-2 w-full mb-4 rounded border-teal-500 relative overflow-y-auto relative"
            @mouseup="getSelectionWithinInput">
            <div class="text-center text-base absolute p-10" v-if="currentSentence" v-html="currentSentence"></div>
            <div class="text-center text-base z-10 absolute p-10" v-if="currentSentenceLayer" v-html="currentSentenceLayer"></div>
          </div>
        
        <!-- END OF SENTENCE DISPLAY PART -->
        <!-- NAVIGATION BUTTON -->
        <div>
          <button class="button-direction" @click="previousSentence"><i class="fas fa-arrow-left text-xl text-teal-500"></i></button>
          <p class="font-bold bg-white p-2 inline" v-if="sentences">{{ currentIndex + 1 }} / {{ sentences.length }}</p>
          <button class="button-direction" @click="nextSentence"><i class="fas fa-arrow-right text-xl text-teal-500"></i></button>
        </div>
        <!-- END OF NAVIGATION BUTTON -->
      </div>
      <div class="flex w-5/12 flex-col px-8 pt-4 items-center h-full">
        <div class="flex px-16 justify-between items-center w-full mb-2">
          <h1 class="font-black text-xl text-teal-900">Sentence</h1>
          <!-- TOGGLE SCORE BUTTON -->
          <label for="toggle" class="flex items-center text-teal-900">
            Show Score
            <toggle-button
              id="toggle" class="ml-2" :color="{checked: '#2C7A7B', unchecked: '#B2F5EA', disabled: '#CCCCCC'}"
              :switch-color="{checked: '#B2F5EA', unchecked: '#2C7A7B'}" @change="toggleScore"></toggle-button>
          </label>
        </div>
        <!-- TABLE HEADER -->
        <thead class="w-full">
          <tr class="w-full flex">
            <th class="w-2/5 py-4 bg-teal-200 font-bold uppercase text-xs text-teal-700 border-b border-teal-300">Entity Concerned</th>
            <th class="w-3/5 py-4 bg-teal-200 font-bold uppercase text-xs text-teal-700 border-b border-teal-300">Words</th>
            <th class="w-40 py-4 bg-teal-200 font-bold uppercase text-xs text-teal-700 border-b border-teal-300" v-if="showScore">Score</th>
          </tr>
        </thead>
        <!-- TABLE BODY -->
        <tbody class="w-full overflow-y-auto" v-if="sentences">
          <tr class="w-full flex border-b border-grey-100" 
            :class="[(items.id === currentEditing) && 'bg-gray-200', (items.flags < 0) && 'hidden']" 
            v-for="items in sentences[currentIndex].elements" 
            :key="items.id" @click="currentEditing = items.id">
            <!-- ENTITY CONCERNED -->
            <td class="w-1/2 py-4 px-2 border-r border-l border-gray-300" :class="[currentEditing == items.id ? 'pointer-events-auto' : 'pointer-events-none']">
              <entity-concerned 
                :value="items.main_words" 
                :deleteEntityConcerned="deleteEntityConcerned" 
                :index="items.id" 
                :startEditingEntityConcerned="startEditingEntityConcerned"
                :finishEditingEntityConcerned="finishEditingWord"></entity-concerned>
            </td>
            <!-- WORDS -->
            <td 
              class="w-1/2 py-4 px-2 border-r border-gray-300 relative" >
              <draggable
                :list="items.words"
                @end="hightlightIt(sentences[currentIndex].complete_sentence)"
                v-bind="dragOptions"
                group="words">
                <!-- change item.content to content -->
                <words 
                v-for="(item, idx) in items.words" 
                :checkerOne="items.position_main_word[0]"
                :checkerTwo="item.positions"
                :value="item.sequences" 
                :key="idx + Date.now()"
                :index="idx"
                :bolderId="item.id"
                :globalIndex="highlightedIndex"
                :ids="items.id"
                :itemId="currentEditing"
                :startEditingWord="startEditingWord"
                :finishEditingWord="finishEditingWord"
                :deleteWord="deleteWord"></words>
              </draggable>
              <button 
                class="button-plusmin text-teal-200 w-full h-6 bg-teal-700 focus:outline-none flex items-center justify-center rounded"
                @click="addWord">
                <i class="fas fa-plus text-sm"></i>
              </button>
            </td>
            <!-- SCORE -->
            <td class="w-40 py-4 px-2 border-r border-gray-300" v-if="showScore">
              <p v-for="(item, idx) in items.sequences" :key="idx + Date.now()" class="h-8 text-center mb-1">score</p>
            </td>
          </tr>
        </tbody>
      </div>
    </div>
    <!-- ACTION BUTTON -->
    <div class="flex m-3 items-center justify-center">
      <button @click="getSentences" class="button-action w-24 h-10 rounded mx-4 bg-teal-700 text-teal-200" :disabled="editingGlobal">
        Display</button>
      <button @click="saveResultAction" class="button-action w-24 h-10 rounded mx-4 bg-teal-700 text-teal-200" :disabled="!editingGlobal">
        Save</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ToggleButton } from 'vue-js-toggle-button';
import draggable from 'vuedraggable'
import _ from 'lodash';

import entityTypes from '@/entityTypes';
import { baseUrl } from '@/config';
import Radio from '@/components/Radio';
import EntityConcerned from '@/components/EntityConcerned';
import Words from '@/components/Words';
import { realCoRefData } from '@/data';
import { makeid, colorizer, getWordsIndexCoordinate, joinArrayRange } from '@/helpers';

export default {
  data() {
    return {
      currentIndex: 0,
      currentSentence: '',
      currentSentenceLayer: '',
      editingGlobal: false,
      sentences: '',
      highlighted: 'No Highlighted word',
      onHighlighting: false,
      highlightedIndex: null,
      showScore: false,
      editWord: false,
      currentEditing: null,
      pollInEdit: null,
      bolder: true,
    }
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: "description",
        disabled: false,
        ghostClass: "ghost"
      };
    },
  },
  components: {
    Radio,
    ToggleButton,
    EntityConcerned,
    Words,
    draggable
  },
  methods: {
    /**
     * process to higlight namedEntity and give color
     * and then set the currentSentence to displayed on the screen
     * @param {string} text - Text to be highlighted
     */
    hightlightIt(text) {
      console.log('boo')
      if(!text)
        return 'No Text';

      let str = text
      const data = this.sentences[this.currentIndex].elements.filter(item => item.id === this.currentEditing)[0];

      if (data.main_words.length > 0) {
        str = joinArrayRange(str.split(/ (?![^<]*<\/span>)/), data.position_main_word[0][0], data.position_main_word[0][1], '#63B3ED');
      }

      for (let count = 0; count < data.words.length; count++) {
        const words = data.words[count];
        if (words.positions[0] !== null && data.position_main_word.length > 0) {
          if (!(words.positions[0] === data.position_main_word[0][0] && words.positions[1] === data.position_main_word[0][1])) {
            str = joinArrayRange(str.split(/ (?![^<]*<\/span>)/), words.positions[0], words.positions[1], '#81E6D9', false, words.id);
          }
        } else if (words.positions[0] !== null) {
          str = joinArrayRange(str.split(/ (?![^<]*<\/span>)/), words.positions[0], words.positions[1], '#81E6D9', false, words.id);
        }
      }

      this.currentSentence = str;
    },
    /**
     * Navigations
     * - pgaeChanges -> helpers
     * - nextSentence -> go to next sentence
     * - previousSentence -> go to previous sentence
     */
    pageChanges() {
      this.currentSentence = this.sentences[this.currentIndex].complete_sentence;
      this.currentSentenceLayer = this.sentences[this.currentIndex].complete_sentence;
      this.currentEditing = null;
      this.highlightedIndex = null;
      this.pollInEdit = null;
    },
    nextSentence() {
      if (this.currentIndex < this.sentences.length - 1) {
        this.currentIndex += 1;
        this.pageChanges();
      }
    },
    previousSentence() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1;
        this.pageChanges();
      }
    },
    /**
     * to get the user selection on the text display
     * it get the selection text with window.selection
     * it also acquire the range of character index of text by getRangeAt()
     * divided processing for words and entity concerned
     */
    getSelectionWithinInput() {
      let selection = '';
      let position = '';
      if (window.getSelection().toString()) {
        const start = window.getSelection().getRangeAt(0).startOffset;
        const end = window.getSelection().getRangeAt(0).endOffset;
        
        selection = window.getSelection().toString();
        position = getWordsIndexCoordinate(this.sentences[this.currentIndex].complete_sentence, start, end)

        //console.log(selection + ' ' + position + ' start:' + start + ' end:' + end);
      };

      if (this.pollInEdit === 'entity') {
        for (let item of this.sentences[this.currentIndex].elements) {
          if (item.id === this.currentEditing) {
            item.main_words = [selection];
            item.position_main_word[0] = position;
            break;
          }
        }
      } else if (this.pollInEdit === 'word') {
        for (let item of this.sentences[this.currentIndex].elements) {
          if (item.id === this.currentEditing) {
            const words = _.cloneDeep(item.words);
            words[this.highlightedIndex].sequences = selection.split(' ');
            words[this.highlightedIndex].positions = position;
            item.words = words;
            break;
          }
        }
      }
    },
    addWord() {
      this.highlightedIndex = 0;
      this.pollInEdit = 'word';
      for (let item of this.sentences[this.currentIndex].elements) {
        if (item.id === this.currentEditing) {
          const words = _.cloneDeep(item.words);
          words.unshift({ 
            id: makeid(8), 
            sequences: [null],
            positions: [null, null]
          });

          item.words = words;
          break;
        }
      }
    },
    getSentences() {
      // USE THIS CODE PART FOR REAL DATA
      // axios.get(baseUrl + 'CHANGE FOR LOCAL ENDPOINT')
      //   .then(response => {
      //     this.sentences = response.data;
      //     this.currentSentence = this.sentences[0].complete_sentence;
      //     this.currentSentenceLayer = this.sentences[0].complete_sentence;
      //     this.editingGlobal = true;
      //   })
      //   .catch(error => {
      //     this.$swal({
      //       type: 'error',
      //       title: 'Oops',
      //       text: 'Your request is failed'
      //     });
      //   });
      
      // USE THIS CODE PART FOR WORKING WITH DUMMY DATA
      this.sentences = realCoRefData;
      this.currentSentence = this.sentences[0].complete_sentence;
      this.currentSentenceLayer = this.sentences[0].complete_sentence;
      this.editingGlobal = true;
      this.$emit('isReady', true); 
      
      
    },
    saveResultAction() {
      // USE THIS CODE PART FOR REAL DATA
      // axios.post(baseUrl + 'CHANGE FOR LOCAL ENDPOINT', this.sentences)
      //   .then(resp => {
      //     this.savedResult = resp.data.score;
      //     this.editingGlobal = false;
      //     this.currentSentenceLayer = '';
      //     this.sentences = '',
      //     this.currentSentence = '';
      //     this.currentIndex = 0;
      //     this.currentEditing = null;
      //     this.$swal({
      //         type: 'success',
      //         title: 'Save Result',
      //         text: 'The train result is' + this.savedResult
      //     });
      //   })
      //   .catch(err => {
      //     this.$swal({
      //       type: 'error',
      //       title: 'Oops',
      //       text: 'Your request is failed'
      //     });
      //   });

      // USE THIS CODE PART FOR WORKING WITH DUMMY DATA
      this.editingGlobal = false;
      this.currentSentenceLayer = '';
      this.sentences = '',
      this.currentSentence = '';
      this.currentIndex = 0;
      this.currentEditing = null;
    },
    toggleScore() {
      this.showScore = !this.showScore;
    },
    deleteWord(index) {
      for (let x of this.sentences[this.currentIndex].elements) {
        if (x.id === this.currentEditing) {
          const del = x.words.filter(item => item.id !== index);
          x.words = del;
        }
      }
      this.hightlightIt(this.sentences[this.currentIndex].complete_sentence)
    },
    startEditingWord(idx, poll) {
      this.highlightedIndex = idx;
      this.pollInEdit = poll;
    },
    finishEditingWord() {
      this.highlightedIndex = null;
      this.pollInEdit = null;
      this.hightlightIt(this.sentences[this.currentIndex].complete_sentence);
    },
    deleteEntityConcerned(id) {
      this.$swal({
        title: 'Are you sure?', text: 'You can\'t revert your action', type: 'warning',
        showCancelButton: true, confirmButtonText: 'Yes Delete it!', cancelButtonText: 'No, Keep it!',
        showCloseButton: true, showLoaderOnConfirm: true
      }).then((result) => {
        if(result.value) {
          this.sentences[this.currentIndex].elements = this.sentences[this.currentIndex].elements.filter(item => item.id !== id);
          this.currentEditing = null;
          this.$swal('Deleted', 'You successfully deleted the record', 'success')
        } else {
          this.$swal('Cancelled', 'Your record is still intact', 'info')
        }
      })
    },
    startEditingEntityConcerned(idx, poll) {
      this.pollInEdit = poll;
    },
  },
  watch: {
    currentEditing: function() {
      if (this.currentSentence) {
        if (this.currentEditing) {
          this.hightlightIt(this.sentences[this.currentIndex].complete_sentence);
        } else {
          this.currentSentence = this.sentences[this.currentIndex].complete_sentence;
        }
      }
    }
  }
}
</script>

<style></style>
